/**
 * Gateway URLs for browser-direct calls (Playground, Models quick-start snippets).
 *
 * Deliberately NOT the same-origin `/api/*` pattern every other endpoint in
 * this app uses (see api/client.ts's own "LOCKED... never an absolute
 * origin" note) — this is the one intentional exception. `llmr-docs/contracts
 * /openapi/member-api.yaml` states outright that member-api "is NOT the
 * inference API... customer inference goes to the gateway at /v1/*, not
 * here", so these pages call the gateway straight from the browser, the same
 * way a customer's own integration would.
 *
 * **Domain only** comes from `PUBLIC_GATEWAY_URL` (Docker build-arg in
 * devops/develop/jenkins-deploy, or `.env.local` locally). Path suffixes
 * (`/v1`, `/v1/chat/completions`, …) are the fixed OpenAI-compatible contract
 * and are assembled here — nowhere else in the app should append them.
 *
 * Undefined when the build-arg is omitted — same "no build-arg means no
 * feature" pattern as `websiteUrl()`. Dev and UAT bake this in via
 * devops/{develop,uat}/jenkins-deploy so Quick Start snippets include a
 * real gateway origin.
 *
 * Playground is gated separately by `PUBLIC_PLAYGROUND` (dev only) so baking
 * a gateway URL for snippets does not also ship the in-portal tester.
 */
export function gatewayUrl(): string | null {
  const url = import.meta.env.PUBLIC_GATEWAY_URL;
  return url ? url : null;
}

/** In-portal Playground — opt-in per deployed build, independent of snippet URLs.
 * Local `npm run dev` still shows it whenever a gateway origin is set.
 */
export function playgroundEnabled(): boolean {
  if (!gatewayUrl()) return false;
  const flag = import.meta.env.PUBLIC_PLAYGROUND;
  if (flag) return flag === '1' || flag === 'true' || flag === 'yes';
  return import.meta.env.DEV;
}

/** Gateway origin without a trailing slash, or null when unset. */
export function gatewayBaseUrl(): string | null {
  const url = gatewayUrl();
  return url ? url.replace(/\/$/, '') : null;
}

/** OpenAI-compatible base URL for official SDK clients (`…/v1`). */
export function gatewayOpenAiBaseUrl(): string | null {
  const base = gatewayBaseUrl();
  return base ? `${base}/v1` : null;
}

/** Full chat-completions endpoint for curl and raw HTTP calls. */
export function gatewayChatCompletionsUrl(): string | null {
  const base = gatewayBaseUrl();
  return base ? `${base}/v1/chat/completions` : null;
}

/** Playground connection-test probe. */
export function gatewayHealthUrl(): string | null {
  const base = gatewayBaseUrl();
  return base ? `${base}/health/liveliness` : null;
}